/** @format */

import {ReplyIcon} from '@heroicons/react/solid'
import React, {
  ComponentPropsWithoutRef,
  ComponentType,
  ReactElement,
} from 'react'

import {PluginKind} from '@src/types/Plugin'
import {PassThroughFC} from '@src/typeUtils'

type AppLogoProps = ComponentPropsWithoutRef<'i'> & {
  fontSize?: string
}

// Omit, because <i> has its own string-only `content` attribute that would
// otherwise swallow the svg half of the union.
const AppLogo: React.FC<
  Omit<AppLogoProps, 'content'> & {
    content: string | ReactElement
  }
> = ({content, fontSize = 'xx-large', ...rest}) => {
  return (
    <i
      {...rest}
      style={{
        display: 'inline-block',
        fontFamily: 'logofont',
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize,
        lineHeight: '1',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      }}
    >
      {content}
    </i>
  )
}

// logofont has no glyph for the two plugins that are not a third-party app, so
// they are SVGs instead. Sized in em so fontSize scales them like a glyph.
// A font glyph is one color, so these only use currentColor too.
const SVG_STYLE = {display: 'block', width: '1em', height: '1em'} as const

export const AutoresponseIcon: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={<ReplyIcon style={SVG_STYLE} />} />
}

// The "webhook" icon from Lucide, which is the shape most dashboards use for
// one. Stroked, unlike the solid marks beside it, so the stroke is a touch
// heavier than Lucide's default 2 to match their weight.
//
// ISC License. Copyright (c) 2026 Lucide Icons and Contributors.
//
// Permission to use, copy, modify, and/or distribute this software for any
// purpose with or without fee is hereby granted, provided that the above
// copyright notice and this permission notice appear in all copies.
//
// THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
// WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
// MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
// ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
// WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
// ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
// OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
export const WebhookIcon: PassThroughFC = ({...rest}) => {
  return (
    <AppLogo
      {...rest}
      content={
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={SVG_STYLE}
        >
          <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" />
          <path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" />
          <path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" />
        </svg>
      }
    />
  )
}

export const ActiveCampaignLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e919)} />
}
export const AirtableLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e907)} />
}
export const AsanaLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e917)} />
}
export const ConstantContactLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e916)} />
}
export const ConvertkitLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e909)} />
}

export const DiscordLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90b)} />
}

export const FreshdeskLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90c)} />
}

export const GithubLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e913)} />
}

export const GladlyLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e910)} />
}

export const GoogleLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e900)} />
}

export const GorgiasLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90d)} />
}

export const HubspotLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e908)} />
}

export const KlaviyoLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e911)} />
}

export const MailchimpLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e901)} />
}

export const MailIcon: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e912)} />
}

export const NotionLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e914)} />
}

export const MailerliteLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90a)} />
}

export const PipedriveLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90e)} />
}

export const RedshiftLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e915)} />
}

export const SalesforceLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e918)} />
}

export const SlackLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e905)} />
}

export const StripeLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e904)} />
}

export const TelegramLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e90f)} />
}

export const LinearLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e91c)} />
}

export const TrelloLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e902)} />
}

export const WhatsappLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e91a)} />
}

export const ZapierLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e906)} />
}

export const ZendeskLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e903)} />
}

export const ZohoCrmLogo: PassThroughFC = ({...rest}) => {
  return <AppLogo {...rest} content={String.fromCharCode(0x0e91b)} />
}

export function getAppLogoByPluginKind(
  pluginKind: PluginKind,
): ComponentType<AppLogoProps> | null {
  return appLogoByPluginKind[pluginKind]
}

const appLogoByPluginKind: Readonly<
  Record<PluginKind, ComponentType<AppLogoProps> | null>
> = Object.freeze({
  airtable: AirtableLogo,
  asana: AsanaLogo,
  autoresponse: AutoresponseIcon,
  constantcontact: ConstantContactLogo,
  convertkit: ConvertkitLogo,
  discord: DiscordLogo,
  email: MailIcon,
  freshdesk: FreshdeskLogo,
  github: GithubLogo,
  gladly: GladlyLogo,
  'google-sheets': GoogleLogo,
  gorgias: GorgiasLogo,
  hubspot: HubspotLogo,
  activecampaign: ActiveCampaignLogo,
  klaviyo: KlaviyoLogo,
  linear: LinearLogo,
  mailchimp: MailchimpLogo,
  mailerlite: MailerliteLogo,
  notion: NotionLogo,
  pipedrive: PipedriveLogo,
  redshift: RedshiftLogo,
  salesforce: SalesforceLogo,
  slack: SlackLogo,
  stripe: StripeLogo,
  stripe_v2: StripeLogo,
  telegram: TelegramLogo,
  trello: TrelloLogo,
  webhook: WebhookIcon,
  whatsapp: WhatsappLogo,
  zapier: ZapierLogo,
  zendesk: ZendeskLogo,
  zohocrm: ZohoCrmLogo,
})
